import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../../services/api';

const steps = ['Nhập email', 'Xác thực mã', 'Đặt lại mật khẩu'];

const ForgotPassword: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.forgotPassword(email);
      toast.success(response.message);
      setActiveStep(1);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Gửi mã thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Mã xác thực phải có 6 chữ số');
      return;
    }

    try {
      setLoading(true);
      const response = await api.verifyResetCode({ email, code });
      toast.success(response.message);
      setActiveStep(2);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Mã xác thực không đúng hoặc đã hết hạn');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Mật khẩu không khớp');
      return;
    }

    try {
      setLoading(true);
      const response = await api.resetPassword({
        email,
        code,
        newPassword
      });
      toast.success(response.message);
      navigate('/login');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Đặt lại mật khẩu thất bại');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <form onSubmit={handleRequestCode}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading || !email}
              >
                {loading ? <CircularProgress size={24} /> : 'Gửi mã xác thực'}
              </Button>
            </Box>
          </form>
        );
      case 1:
        return (
          <form onSubmit={handleVerifyCode}>
            <TextField
              fullWidth
              label="Mã xác thực"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              margin="normal"
              inputProps={{ maxLength: 6 }}
              required
            />
            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading || code.length !== 6}
              >
                {loading ? <CircularProgress size={24} /> : 'Xác thực'}
              </Button>
            </Box>
          </form>
        );
      case 2:
        return (
          <form onSubmit={handleResetPassword}>
            <TextField
              fullWidth
              label="Mật khẩu mới"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Xác nhận mật khẩu"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
            />
            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
              >
                {loading ? <CircularProgress size={24} /> : 'Đặt lại mật khẩu'}
              </Button>
            </Box>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Quên mật khẩu
        </Typography>
        <Stepper activeStep={activeStep} sx={{ my: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent(activeStep)}
      </Paper>
    </Container>
  );
};

export default ForgotPassword; 